import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ImagePlus, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  AffidavitRow,
  AffidavitsApi,
  CreateAffidavitData,
  UpdateAffidavitData,
  UploadApi,
} from '@/services/school.api';
import { AcademicClassesApi } from '@/services/academic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Field, Select } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDate } from '@/lib/format';

function errMsg(e: unknown): string {
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong. Please try again.';
}

/**
 * Affidavits — school-tier admin CRUD. The one real gap confirmed by
 * survey (migration, DDL, and the parent app's own get-affidavits/
 * submit-affidavit endpoints already exist and work) — this page reads/
 * writes the SAME table those mobile endpoints already use. One row per
 * student: picking a student who already has an affidavit here overwrites
 * it, matching legacy's own updateOrCreate behavior and the mobile write
 * path's own upsert.
 */
export function AffidavitsPage() {
  const { can } = usePermissions();
  const canWrite = can('/affidavits', 'create');
  const canDelete = can('/affidavits', 'delete');
  const qc = useQueryClient();

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AffidavitRow | null>(null);
  const [deleting, setDeleting] = useState<AffidavitRow | null>(null);

  const { data: rows = [], isFetching } = useQuery({
    queryKey: ['affidavits'],
    queryFn: AffidavitsApi.list,
  });

  const remove = useMutation({
    mutationFn: (id: string) => AffidavitsApi.remove(id),
    onSuccess: () => {
      setDeleting(null);
      qc.invalidateQueries({ queryKey: ['affidavits'] });
    },
  });

  return (
    <>
      <PageHeader
        title="Affidavits"
        description="Student and guardian signed affidavits."
        actions={
          canWrite && (
            <button className="btn-primary flex items-center gap-1.5" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" /> Create Affidavit
            </button>
          )
        }
      />

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Student</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Class Section</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Guardian</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Signatures</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Date</th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white">
            {isFetching && !rows.length ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-slate-400">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-slate-400">
                  No affidavits yet.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5 text-slate-700">{r.studentName}</td>
                  <td className="px-4 py-2.5 text-slate-600">{r.className} · {r.sectionName}</td>
                  <td className="px-4 py-2.5 text-slate-600">{r.guardianName ?? '—'}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1.5">
                      {r.studentSignatureUrl ? (
                        <img src={r.studentSignatureUrl} alt="Student signature" title="Student signature" className="h-8 w-14 rounded border border-slate-200 object-contain" />
                      ) : (
                        <span className="text-xs text-slate-400">no student sig</span>
                      )}
                      {r.guardianSignatureUrl ? (
                        <img src={r.guardianSignatureUrl} alt="Guardian signature" title="Guardian signature" className="h-8 w-14 rounded border border-slate-200 object-contain" />
                      ) : (
                        <span className="text-xs text-slate-400">no guardian sig</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">{formatDate(r.createdAt)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <span className="inline-flex items-center gap-2">
                      {canWrite && (
                        <button
                          type="button"
                          title="Edit"
                          className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-brand-700"
                          onClick={() => setEditing(r)}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          title="Delete"
                          className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600"
                          onClick={() => setDeleting(r)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal open={creating} onClose={() => setCreating(false)} title="Create Affidavit" size="md">
        <CreateAffidavitForm onDone={() => setCreating(false)} />
      </Modal>

      <Modal open={!!editing} onClose={() => setEditing(null)} title="Edit Affidavit" size="md">
        {editing && <EditAffidavitForm affidavit={editing} onDone={() => setEditing(null)} />}
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        title="Delete affidavit?"
        message={`This will permanently remove ${deleting?.studentName ?? 'this'}'s affidavit and both signature files. This cannot be undone.`}
        confirmText="Delete"
        destructive
        loading={remove.isPending}
      />
    </>
  );
}

// ───── Signature upload control — shared by create/edit forms ──────────────
function SignatureUpload({
  label,
  imageUrl,
  onChange,
}: {
  label: string;
  imageUrl: string;
  onChange: (url: string) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | undefined>();

  async function handlePick(file: File) {
    setUploading(true);
    setUploadError(undefined);
    try {
      const res = await UploadApi.upload(file);
      onChange(res.url);
    } catch (e) {
      setUploadError(errMsg(e));
    } finally {
      setUploading(false);
    }
  }

  return (
    <Field label={label} required={!imageUrl}>
      {imageUrl ? (
        <div className="relative">
          <img src={imageUrl} alt="" className="h-24 w-full rounded-md border border-slate-200 object-contain" />
          <label className="absolute inset-x-0 bottom-0 flex cursor-pointer items-center justify-center gap-1.5 rounded-b-md bg-black/50 py-1 text-xs text-white hover:bg-black/60">
            <ImagePlus className="h-3.5 w-3.5" /> Replace
            <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && handlePick(e.target.files[0])} />
          </label>
        </div>
      ) : (
        <label className="flex h-24 w-full cursor-pointer flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed border-slate-300 text-xs text-slate-500 hover:border-brand-400 hover:text-brand-600">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
          {uploading ? 'Uploading…' : 'Upload signature'}
          <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => e.target.files?.[0] && handlePick(e.target.files[0])} />
        </label>
      )}
      {uploadError && <p className="mt-1 text-xs text-red-600">{uploadError}</p>}
    </Field>
  );
}

// ───── Class → Section → Student cascading picker ───────────────────────────
function ClassSectionStudentPicker({
  classId,
  onClassChange,
  sectionId,
  onSectionChange,
  studentId,
  onStudentChange,
}: {
  classId: string;
  onClassChange: (v: string) => void;
  sectionId: string;
  onSectionChange: (v: string) => void;
  studentId: string;
  onStudentChange: (v: string) => void;
}) {
  const { data: classesPage } = useQuery({
    queryKey: ['academic-classes', 'all-for-affidavits'],
    queryFn: () => AcademicClassesApi.list({ limit: 100 }),
  });
  const classes = classesPage?.items ?? [];
  const sections = classes.find((c) => c.id === classId)?.sections ?? [];

  const { data: students = [] } = useQuery({
    queryKey: ['affidavits-students-by-section', sectionId],
    queryFn: () => AffidavitsApi.studentsBySection(sectionId),
    enabled: !!sectionId,
  });

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <Field label="Class" required>
        <Select
          value={classId}
          onChange={(e) => {
            onClassChange(e.target.value);
            onSectionChange('');
            onStudentChange('');
          }}
        >
          <option value="">Select class</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Section" required>
        <Select
          value={sectionId}
          onChange={(e) => {
            onSectionChange(e.target.value);
            onStudentChange('');
          }}
          disabled={!classId}
        >
          <option value="">Select section</option>
          {sections.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Student" required>
        <Select value={studentId} onChange={(e) => onStudentChange(e.target.value)} disabled={!sectionId}>
          <option value="">Select student</option>
          {students.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </Field>
    </div>
  );
}

function CreateAffidavitForm({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [studentId, setStudentId] = useState('');
  const [studentSignatureUrl, setStudentSignatureUrl] = useState('');
  const [guardianSignatureUrl, setGuardianSignatureUrl] = useState('');

  const create = useMutation({
    mutationFn: () =>
      AffidavitsApi.create({ studentId, studentSignatureUrl, guardianSignatureUrl }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['affidavits'] });
      onDone();
    },
  });

  const valid = !!studentId && !!studentSignatureUrl && !!guardianSignatureUrl;

  return (
    <div className="space-y-4">
      <ClassSectionStudentPicker
        classId={classId}
        onClassChange={setClassId}
        sectionId={sectionId}
        onSectionChange={setSectionId}
        studentId={studentId}
        onStudentChange={setStudentId}
      />
      <p className="text-xs text-slate-500">The guardian is resolved automatically from the selected student.</p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <SignatureUpload label="Student Signature" imageUrl={studentSignatureUrl} onChange={setStudentSignatureUrl} />
        <SignatureUpload label="Guardian Signature" imageUrl={guardianSignatureUrl} onChange={setGuardianSignatureUrl} />
      </div>

      {create.error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(create.error)}</div>}

      <div className="flex justify-end gap-2 pt-2">
        <button className="btn-secondary" onClick={onDone}>
          Cancel
        </button>
        <button className="btn-primary" disabled={!valid || create.isPending} onClick={() => create.mutate()}>
          {create.isPending ? 'Saving…' : 'Create'}
        </button>
      </div>
    </div>
  );
}

function EditAffidavitForm({ affidavit, onDone }: { affidavit: AffidavitRow; onDone: () => void }) {
  const qc = useQueryClient();
  const [studentSignatureUrl, setStudentSignatureUrl] = useState(affidavit.studentSignatureUrl ?? '');
  const [guardianSignatureUrl, setGuardianSignatureUrl] = useState(affidavit.guardianSignatureUrl ?? '');

  const save = useMutation({
    mutationFn: () => {
      const data: UpdateAffidavitData = {};
      if (studentSignatureUrl !== affidavit.studentSignatureUrl) data.studentSignatureUrl = studentSignatureUrl;
      if (guardianSignatureUrl !== affidavit.guardianSignatureUrl) data.guardianSignatureUrl = guardianSignatureUrl;
      return AffidavitsApi.update(affidavit.id, data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['affidavits'] });
      onDone();
    },
  });

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        {affidavit.studentName} · {affidavit.className} · {affidavit.sectionName}
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <SignatureUpload label="Student Signature" imageUrl={studentSignatureUrl} onChange={setStudentSignatureUrl} />
        <SignatureUpload label="Guardian Signature" imageUrl={guardianSignatureUrl} onChange={setGuardianSignatureUrl} />
      </div>

      {save.error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(save.error)}</div>}

      <div className="flex justify-end gap-2 pt-2">
        <button className="btn-secondary" onClick={onDone}>
          Cancel
        </button>
        <button
          className="btn-primary"
          disabled={!studentSignatureUrl || !guardianSignatureUrl || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}
